import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { BrandMark } from "../components/BrandMark";
import { PublicMobileMenu } from "../components/PublicMobileMenu";
import { toast } from "sonner";
import {
    Menu, X, ArrowRight, Wallet, Briefcase, Users, CheckCircle2,
    FileSearch, ClipboardCheck, ShieldCheck, FileText, Banknote,
    Info, Facebook, Instagram, Linkedin, MessageCircle,
} from "lucide-react";

/* ---------- nav (Loans routes to /loans; About Us to /about-us; others to /home) ---------- */
const NAV = [
    { label: "Home", to: "/home" },
    { label: "About Us", to: "/about-us" },
    { label: "Loans", to: "/loans" },
    { label: "Saving Scheme", to: "/daily-savings" },
    { label: "Investment", to: "/investment" },
    { label: "Careers", to: "/careers" },
    { label: "Contact Us", to: "/contact-us" },
];

const PRODUCTS = [
    {
        icon: Wallet,
        title: "Personal Loan",
        suitable: "Individuals with approved personal requirements.",
        features: [
            "Financial support for approved personal needs",
            "Structured repayment schedule",
            "Digital records and receipts",
            "Clear statement of charges",
        ],
        review: "Applications are reviewed based on submitted information, documents and eligibility.",
        terms: "Loan amount, tenure and terms are set at the time of admin approval.",
    },
    {
        icon: Briefcase,
        title: "Business Loan",
        suitable: "Small businesses, self-employed and working-capital needs.",
        features: [
            "Support for approved business requirements",
            "Repayment plans aligned to business cycles where applicable",
            "Ongoing account access through digital records",
            "Structured documentation process",
        ],
        review: "Business profile, documents and repayment ability are considered during review.",
        terms: "Sanctioned amount, tenure and applicable terms are finalised by admin approval.",
    },
    {
        icon: Users,
        title: "Group Loan",
        suitable: "Eligible joint-liability groups working towards a shared objective.",
        features: [
            "Structured financial support for member groups",
            "Shared repayment schedule",
            "Group meetings and recorded contributions",
            "Digital account records for each member",
        ],
        review: "Group formation, member details and group readiness are part of the review.",
        terms: "Group loan amount, tenure and terms are subject to admin approval.",
    },
];

const PROCESS = [
    { icon: FileText, title: "Application" },
    { icon: FileSearch, title: "Verification" },
    { icon: ClipboardCheck, title: "Admin Review" },
    { icon: ShieldCheck, title: "Documents" },
    { icon: Banknote, title: "Disbursement" },
];

const FOOTER_LINKS = {
    Products: ["Personal Loan", "Business Loan", "Group Loan", "Saving Scheme", "Investment"],
    Company: ["About Us", "Careers", "News & Updates", "Contact Us"],
    Support: ["FAQs", "Grievance Redressal", "Customer Care", "Downloads"],
};

const placeholderLoginToast = () => {
    window.location.assign("/customer-login");
};

const learnMoreToast = (title) =>
    toast.info(`${title} — details on request.`, {
        description: "Please contact us to review eligibility, documents and applicable terms.",
    });

/* ---------- page ---------- */
export default function PublicLoans() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        document.title = "Loans | Lokdhara Microfinance";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    const go = (to) => { if (to) navigate(to); };

    return (
        <div id="top" className="min-h-screen bg-white text-slate-900 antialiased">
            {/* HEADER --------------------------------------------------- */}
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
                    <Link to="/home" className="flex items-center" data-testid="loans-brand">
                        <BrandMark size={56} className="sm:!h-14" />
                    </Link>
                    <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-slate-700">
                        {NAV.map((n) => (
                            <button key={n.label}
                                onClick={() => go(n.to)}
                                className={`hover:text-brand-royal transition ${n.to === "/loans" ? "text-brand-royal" : ""}`}
                                data-testid={`loans-nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        <Button
                            className="hidden sm:inline-flex bg-brand-royal hover:bg-brand-hover text-white rounded-full h-10 px-5"
                            onClick={placeholderLoginToast}
                            data-testid="loans-header-customer-login"
                        >
                            Customer Login
                        </Button>
                        <button
                            aria-label="Open menu"
                            className="lg:hidden h-10 w-10 rounded-lg border border-slate-200 grid place-items-center"
                            onClick={() => setMenuOpen(true)}
                            data-testid="loans-mobile-menu-btn"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                {menuOpen && (
                    <PublicMobileMenu
                        open={menuOpen}
                        onOpenChange={setMenuOpen}
                        nav={NAV}
                        onNavItemClick={(n) => go(n.to)}
                        onLoginClick={placeholderLoginToast}
                        testId="loans-mobile-menu"
                        activePath="/loans"
                    />
                )}
            </header>

            {/* NAVY HERO ------------------------------------------------ */}
            <section className="relative overflow-hidden bg-brand-navy text-white">
                <div className="absolute inset-0 pointer-events-none opacity-20"
                    style={{ backgroundImage: "radial-gradient(circle at 15% 25%, rgba(255,255,255,0.15) 0%, transparent 45%), radial-gradient(circle at 85% 65%, rgba(255,255,255,0.10) 0%, transparent 40%)" }}
                    aria-hidden
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-tint bg-white/10 rounded-full px-3 py-1">
                        Our Loan Products
                    </span>
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight max-w-3xl">
                        Loan Solutions for Different Needs
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-200/90 max-w-2xl leading-relaxed">
                        Structured loan options designed around individuals, small businesses and
                        joint-liability groups — reviewed and approved on a case-by-case basis.
                    </p>
                </div>
            </section>

            {/* PRODUCT CARDS -------------------------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Product Overview
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            Three loan categories, one clear process
                        </h2>
                    </div>
                    <div className="grid gap-6 lg:grid-cols-3">
                        {PRODUCTS.map(({ icon: Icon, title, suitable, features, review, terms }) => (
                            <Card key={title}
                                className="rounded-2xl border-slate-100 p-7 hover:shadow-card-hover transition flex flex-col"
                                data-testid={`loan-product-${title.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                <div className="h-12 w-12 rounded-xl bg-brand-soft grid place-items-center">
                                    <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <h3 className="mt-5 font-display font-bold text-brand-navy text-xl">{title}</h3>

                                <div className="mt-5">
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Suitable For</div>
                                    <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{suitable}</p>
                                </div>

                                <div className="mt-5">
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Key Features</div>
                                    <ul className="mt-2 space-y-2">
                                        {features.map((f) => (
                                            <li key={f} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                                                <CheckCircle2 className="h-4 w-4 text-brand-royal shrink-0 mt-0.5" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="mt-5">
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Application Review</div>
                                    <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{review}</p>
                                </div>

                                <div className="mt-5">
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Amount, Tenure &amp; Terms</div>
                                    <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{terms}</p>
                                </div>

                                <div className="mt-7 pt-5 border-t border-slate-100">
                                    <Button
                                        className="w-full rounded-full h-11 bg-brand-navy hover:bg-brand-royal text-white"
                                        onClick={() => learnMoreToast(title)}
                                        data-testid={`loan-learn-more-${title.replace(/\s+/g, "-").toLowerCase()}`}
                                    >
                                        Learn More <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS -------------------------------------------------- */}
            <section className="bg-slate-50 py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                            The Process
                        </span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            A clear five-step journey
                        </h2>
                        <p className="mt-3 text-slate-600">
                            Every application follows the same structured review and documentation flow.
                        </p>
                    </div>
                    <div className="grid gap-6 sm:gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        {PROCESS.map(({ icon: Icon, title }, i) => (
                            <div key={title}
                                className="relative flex lg:flex-col items-center lg:items-start gap-4 lg:gap-5 bg-white rounded-2xl p-5 border border-slate-100"
                                data-testid={`loan-process-step-${i + 1}`}
                            >
                                <div className="relative h-14 w-14 rounded-2xl bg-brand-navy grid place-items-center shrink-0">
                                    <Icon className="h-6 w-6 text-white" strokeWidth={1.75} />
                                    <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-brand-royal text-white text-[11px] font-bold grid place-items-center">
                                        {i + 1}
                                    </span>
                                </div>
                                <div className="font-semibold text-slate-900 leading-snug">{title}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* IMPORTANT NOTE ------------------------------------------ */}
            <section className="py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <Card className="rounded-2xl border-brand-soft bg-brand-soft/50 p-6 sm:p-7 flex items-start gap-4"
                        data-testid="loan-important-note"
                    >
                        <div className="h-10 w-10 rounded-xl bg-white grid place-items-center shrink-0">
                            <Info className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-royal">Important Note</div>
                            <p className="mt-2 text-slate-700 leading-relaxed">
                                Loan approval, amount, tenure, charges and terms are subject to review
                                and approval.
                            </p>
                        </div>
                    </Card>
                </div>
            </section>

            {/* CTA ------------------------------------------------------ */}
            <section className="bg-gradient-to-br from-brand-navy to-brand-royal text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        Explore the right loan solution.
                    </h2>
                    <p className="mt-4 text-white/80 max-w-xl mx-auto">
                        Speak with our team to review eligibility and next steps.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button
                            className="bg-white text-brand-navy hover:bg-brand-soft rounded-full h-12 px-6 font-semibold"
                            onClick={() => navigate("/home")}
                            data-testid="loans-cta-contact-btn"
                        >
                            Contact Us <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-full h-12 px-6 border-white/40 text-white hover:bg-white/10 hover:text-white"
                            onClick={placeholderLoginToast}
                            data-testid="loans-cta-customer-login"
                        >
                            Customer Login
                        </Button>
                    </div>
                </div>
            </section>

            {/* FOOTER --------------------------------------------------- */}
            <footer className="bg-brand-navy text-slate-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8">
                    <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
                        <div className="lg:col-span-2">
                            <BrandMark size={40} variant="white" />
                            <p className="mt-5 text-sm leading-relaxed max-w-md text-slate-400">
                                Lokdhara Microfinance is focused on providing structured and accessible
                                financial services through transparent processes, clear records and
                                customer-oriented support.
                            </p>
                            <div className="mt-5 flex gap-3">
                                {[Facebook, Instagram, Linkedin, MessageCircle].map((Icon, i) => (
                                    <a key={i} href="/home"
                                        className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 grid place-items-center transition"
                                        aria-label={`social-${i}`}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        {Object.entries(FOOTER_LINKS).map(([col, items]) => (
                            <div key={col}>
                                <div className="text-white font-semibold text-sm mb-4">{col}</div>
                                <ul className="space-y-2 text-sm">
                                    {items.map((item) => (
                                        <li key={item}>
                                            <button
                                                onClick={() => {
                                                    if (item === "About Us") navigate("/about-us");
                                                    else if (["Personal Loan", "Business Loan", "Group Loan"].includes(item)) navigate("/loans");
                                                    else navigate("/home");
                                                }}
                                                className="hover:text-white transition text-left"
                                            >
                                                {item}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <p className="mt-12 text-xs text-slate-400 leading-relaxed max-w-3xl">
                        Product availability, eligibility, rates and terms are subject to approval and
                        applicable requirements.
                    </p>

                    <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                        <div>© {new Date().getFullYear()} Lokdhara Microfinance. All rights reserved.</div>
                        <div className="flex items-center gap-5">
                            <button onClick={() => navigate("/home")} className="hover:text-white">Privacy Policy</button>
                            <button onClick={() => navigate("/home")} className="hover:text-white">Terms &amp; Conditions</button>
                            <Link
                                to="/login"
                                className="text-slate-300 hover:text-white underline underline-offset-4"
                                data-testid="loans-footer-staff-login"
                            >
                                Staff Login
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
